"use client";
import { IAccountAssets, IBlocks, ITheme, IUserData } from "@/types";
import React from "react";
import { BlockUpdateFunction } from ".";
import AdminPopover from "../../content/AdminPopover";
import EmailOptions, { BlockOptions } from "./tabs/email/options";
import { Separator } from "@/components/ui/separator";
import EmailCustomization from "./tabs/email/customization";
import { toast } from "sonner";
import BlockMetadata from "./tabs/metadata";
import RenderIconOrAsset from "../RenderIconOrAsset";
import IconAssetPicker from "./tabs/iconAssetPicker";

interface Props {
    admin?: boolean;
    user: IUserData;
    block: IBlocks;
    update: BlockUpdateFunction;
    style: any;
    assets: IAccountAssets[];
    theme: ITheme;
    newBlock?: string | null;
}
export default function BlockEmail({
    admin,
    user,
    block,
    update,
    style,
    assets,
    theme,
    newBlock,
}: Props) {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    React.useEffect(() => {
        if (newBlock === block.id) {
            handleExpand();
        }
        return () => {
            window.removeEventListener("closeBlock", handleClose);
        };
    }, [newBlock, block.id]);
    React.useEffect(() => {
        window.addEventListener("closeBlock", {
            handleEvent: (event: CustomEvent) => {
                if (event.detail.id != block.id) {
                    if (isExpanded) {
                        setIsExpanded(false);
                    }
                }
            },
        });
    });
    React.useEffect(() => {
        if (block != blockUpdate) {
            setBlockUpdate(block);
        }
    }, [block]);
    const handleClose = () => {
        setIsExpanded(false);
        window.dispatchEvent(new CustomEvent("collapseBlock"));
    };
    function handleUpdate(value: IBlocks) {
        if (value == null) {
            update(blockUpdate.id, value, true);
            return;
        }
        setBlockUpdate({
            ...blockUpdate,
            ...value,
        });
        if (block == value) return;
        update(blockUpdate.id, value);
    }
    const event = new CustomEvent("expandBlock", {
        detail: {
            id: blockUpdate.id,
            title: "Email",
            titleOptions: (
                <BlockOptions
                    block={blockUpdate}
                    blockUpdate={setBlockUpdate}
                    update={handleUpdate}
                />
            ),
            content: (
                <div className="flex flex-col gap-3 pb-3">
                    <EmailOptions
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                    />
                    <IconAssetPicker
                        account={user}
                        block={block}
                        update={handleUpdate}
                        assets={assets}
                    />
                    <EmailCustomization
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                        assets={assets}
                        user={user}
                    />
                    <BlockMetadata block={blockUpdate} update={handleUpdate} />
                </div>
            ),
        },
    });
    const handleExpand = () => {
        window.dispatchEvent(
            new CustomEvent("closeBlock", {
                detail: {
                    id: block.id,
                },
            })
        );
        setIsExpanded(true);
        window.dispatchEvent(event);
    };
    React.useEffect(() => {
        if (isExpanded) {
            window.dispatchEvent(event);
        }
    }, [blockUpdate]);
    return (
        <AdminPopover
            admin={admin}
            className={block.show ? "" : "opacity-30"}
            onClick={handleExpand}
            title={"Email | " + block.url}
            isOpen={isExpanded}
            onClose={handleClose}
        >
            <div className={`w-full`}>
                <button
                    style={style}
                    id="email-block"
                    className={`{formatClassName(text)} 
                    ${theme?.button_shadow}
                    ${theme?.button_rounded}
        w-full 
        px-1 
        py-3 text-xl
        shadow-md transition-all 
        duration-150 hover:scale-105
        ${block.name.replaceAll(" ", "-")}
        `}
                    onClick={async () => {
                        try {
                            toast.success(
                                `${user.displayname}'s email copied to clipboard`
                            );
                            await navigator.clipboard.writeText(
                                blockUpdate.url
                            );
                        } catch (err) {
                            console.error("Failed to copy text: ", err);
                            toast.error("Failed to copy to clipboard");
                        }
                    }}
                    // style={style}
                >
                    <RenderIconOrAsset
                        account_id={user.id}
                        iconName={blockUpdate.icon}
                        size={26}
                    />
                    <span
                        id="block-text"
                        className={`block w-full px-6 text-center font-bold`}
                    >
                        {blockUpdate.name}
                    </span>
                </button>
            </div>
        </AdminPopover>
    );
}
